import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';

import {
  RQ_ADD_SALARY_ADVANCE_API,
  RQ_DELETE_SALARY_ADVANCE_API,
  RQ_GENERATE_SALARY_ALL_BY_DATE_RANGE_API,
  RQ_GENERATE_SALARY_BY_DATE_RANGE_API,
  RQ_GET_ALL_SALARY_ADVANCE_API,
  RQ_GET_GENERATED_SALARY_BY_EMPLOYEE_ID_API,
  RQ_GET_SALARY_ADVANCE_BY_EMPLOYEE_ID_API,
  RQ_LOOKUP_ALL_SALARY_API,
  RQ_LOOKUP_ALL_SALARY_BY_DATE_RANGE_API,
  RQ_LOOKUP_SALARY_BY_DATE_RANGE_API,
  RQ_LOOKUP_SALARY_BY_EMPLOYEE_ID_API,
  RQ_SALARY_DELETE_API,
  RQ_UPDATE_SALARY_ADVANCE_API,
} from './constants';
import {rqCreateRequest} from './rqHttpUtils';

// ════════════════════════════════════════════════════════════════════════════
// SALARY ADVANCE
// ════════════════════════════════════════════════════════════════════════════

// GET /api/salary-advance/lookupAll
export const RqGetAllSalaryAdvances = (token, options = {}) => {
  return useQuery({
    queryKey:             ['rq_get_all_salary_advances'],
    queryFn:              async () => (await rqCreateRequest(RQ_GET_ALL_SALARY_ADVANCE_API, null, token)) || [],
    refetchOnWindowFocus: false,
    retry:                1,
    ...options,
  });
};

// GET /api/salary-advance/lookupBy/{employeeId}
export const RqGetSalaryAdvanceByEmployeeId = (employeeId, token, options = {}) => {
  return useQuery({
    queryKey:             ['rq_get_salary_advance_by_employee_id', employeeId],
    queryFn:              async () =>
      rqCreateRequest(RQ_GET_SALARY_ADVANCE_BY_EMPLOYEE_ID_API, null, token, {employeeId}),
    enabled:              !!employeeId && !!token,
    refetchOnWindowFocus: false,
    retry:                1,
    ...options,
  });
};

// POST /api/salary-advance/add
export const RqAddSalaryAdvance = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['rq_add_salary_advance'],
    mutationFn:  async ({payload, token}) => {
      if (!token) {throw new Error('Auth token missing');}
      return rqCreateRequest(RQ_ADD_SALARY_ADVANCE_API, payload, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['rq_get_all_salary_advances']});
      queryClient.invalidateQueries({queryKey: ['rq_supervisor_lookup_all']});
    },
    ...options,
  });
};

// PUT /api/salary-advance/update/{id}
export const RqUpdateSalaryAdvance = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['rq_update_salary_advance'],
    mutationFn:  async ({id, payload, token}) => {
      if (!token) {throw new Error('Auth token missing');}
      return rqCreateRequest(RQ_UPDATE_SALARY_ADVANCE_API, payload, token, {id});
    },
    onSuccess: (_, {id}) => {
      queryClient.invalidateQueries({queryKey: ['rq_get_all_salary_advances']});
      queryClient.invalidateQueries({queryKey: ['rq_get_salary_advance_by_employee_id']});
      queryClient.invalidateQueries({queryKey: ['rq_get_salary_advance', id]});
      queryClient.invalidateQueries({queryKey: ['rq_supervisor_lookup_all']});
    },
    ...options,
  });
};

// DELETE /api/salary-advance/delete/{id}
export const RqDeleteSalaryAdvance = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['rq_delete_salary_advance'],
    mutationFn:  async ({id, token}) => {
      if (!token) {throw new Error('Auth token missing');}
      return rqCreateRequest(RQ_DELETE_SALARY_ADVANCE_API, null, token, {id});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['rq_get_all_salary_advances']});
      queryClient.invalidateQueries({queryKey: ['rq_supervisor_lookup_all']});
    },
    ...options,
  });
};

export const RqGenerateSalaryByDateRange = (options = {}) => {
  const queryClient = useQueryClient();
 
  return useMutation({
    mutationKey: ['rq_generate_salary_by_date_range'],
    mutationFn: async ({employeeId, fromDate, toDate, survivalAmount, token}) => {
      if (!token) {throw new Error('Auth token missing');}

      const apiWithQuery = {
        ...RQ_GENERATE_SALARY_BY_DATE_RANGE_API,
        url: `api/salary/generateByDateRange/<%= employeeId %>?fromDate=${fromDate}&toDate=${toDate}&survivalAmount=${survivalAmount ?? 0}`,
      };
 
      return rqCreateRequest(
        apiWithQuery,
        null,        
        token,
        {employeeId}, 
        undefined,   
      );
    },
    onSuccess: (_, {employeeId}) => {
      queryClient.invalidateQueries({queryKey: ['rq_lookup_all_salary']});
      queryClient.invalidateQueries({queryKey: ['rq_get_generated_salary', employeeId]});
      queryClient.invalidateQueries({queryKey: ['rq_supervisor_lookup_all']});
    },
    ...options,
  });
};

export const RqGenerateSalaryAllByDateRange = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['rq_generate_salary_all_by_date_range'],
    mutationFn:  async ({fromDate, toDate, token}) => {
      if (!token) {throw new Error('Auth token missing');}
      return rqCreateRequest(
        RQ_GENERATE_SALARY_ALL_BY_DATE_RANGE_API,
        null,
        token,
        {},
        {fromDate, toDate},
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['rq_lookup_all_salary']});
      queryClient.invalidateQueries({queryKey: ['rq_supervisor_lookup_all']});
    },
    ...options,
  });
};

// GET /api/salary/lookupByDateRange/{employeeId}?fromDate=&toDate=
export const RqLookupSalaryByDateRange = (employeeId, fromDate, toDate, token, options = {}) => {
  return useQuery({
    queryKey:             ['rq_lookup_salary_by_date_range', employeeId, fromDate, toDate],
    queryFn:              async () =>
      rqCreateRequest(RQ_LOOKUP_SALARY_BY_DATE_RANGE_API, null, token, {employeeId}, {fromDate, toDate}),
    enabled:              !!employeeId && !!fromDate && !!toDate && !!token,
    refetchOnWindowFocus: false,
    retry:                1,
    ...options,
  });
};

// GET /api/salary/lookupBy/{employeeId}
export const RqLookupSalaryByEmployeeId = (employeeId, token, options = {}) => {
  return useQuery({
    queryKey:             ['rq_lookup_salary_by_employee_id', employeeId],
    queryFn:              async () =>
      rqCreateRequest(RQ_LOOKUP_SALARY_BY_EMPLOYEE_ID_API, null, token, {employeeId}),
    enabled:              !!employeeId && !!token,
    refetchOnWindowFocus: false,
    retry:                1,
    ...options,
  });
};

// GET /api/salary/lookupAll
export const RqLookupAllSalary = (token, options = {}) => {
  const {month, year, enabled, ...restOptions} = options;
 
  return useQuery({
    queryKey:             ['rq_lookup_all_salary', month, year],  // re-fetches when month/year change
    queryFn:              async () =>
      (await rqCreateRequest(
        RQ_LOOKUP_ALL_SALARY_API,   
        null,                        
        token,
        {},                          
        {month, year},               
      )) || [],
    enabled:              (enabled !== undefined ? enabled : true) && !!token && !!month && !!year,
    refetchOnWindowFocus: false,
    retry:                1,
    ...restOptions,
  });
};

// GET /api/salary/lookupAllByDateRange?fromDate=&toDate=
export const RqLookupAllSalaryByDateRange = (fromDate, toDate, token, options = {}) => {
  return useQuery({
    queryKey:             ['rq_lookup_all_salary_by_date_range', fromDate, toDate],
    queryFn:              async () =>
      (await rqCreateRequest(
        RQ_LOOKUP_ALL_SALARY_BY_DATE_RANGE_API,
        null,
        token,
        {},
        {fromDate, toDate},
      )) || [],
    enabled:              !!fromDate && !!toDate && !!token,
    refetchOnWindowFocus: false,
    retry:                1,
    ...options,
  });
};

// GET /api/salary/api/get/generated/{employeeId}?month=&year=
export const RqGetGeneratedSalaryByEmployeeId = (employeeId, month, year, token, options = {}) => {
  return useQuery({
    queryKey:             ['rq_get_generated_salary', employeeId, month, year],
    queryFn:              async () =>
      rqCreateRequest(
        RQ_GET_GENERATED_SALARY_BY_EMPLOYEE_ID_API,
        null,
        token,
        {employeeId},
        {month, year},
      ),
    enabled:              !!employeeId && !!month && !!year && !!token,
    refetchOnWindowFocus: false,
    retry:                1,
    ...options,
  });
};

export const RqDeleteSalary = (options = {}) => {
  return useMutation({
    mutationFn: async ({salaryId, token}) =>
      rqCreateRequest(
        RQ_SALARY_DELETE_API,
        null,
        token,
        {salaryId},
      ),
    ...options,
  });
};
